import { ReportExportButtons } from '../components/ReportExportButtons'
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { LoginResponse } from '../api/auth'
import { costCentresApi, type CostCentre } from '../api/costs'
import { expenseCategoriesApi, expensesApi, type Expense, type ExpenseCategory } from '../api/finance-ext'
import {
  APPROVER_LEVELS,
  financeControlsApi,
  type AlertsResponse,
  type ApprovalInfo,
  type ApprovalPolicy,
  type ApprovalPolicyBody,
  type ApproverLevel,
  type DuplicatesResponse,
} from '../api/financeControls'
import {
  FormGrid,
  Button,
  Callout,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  DataTable,
  EmptyState,
  FilterBar,
  IconButton,
  LoadingState,
  Page,
  PageHeader,
  Select,
  StatusPill,
  Tabs,
  TextField,
  useConfirm,
} from '../components/ui'

/**
 * F-079 (TASK-0333) — controlli finanziari: matrice di approvazione delle spese, spese da approvare, duplicati, alert e
 * soglie dei soci.
 *
 * <b>Cosa NON c'è</b>: approvazioni dei lotti di pagamento o delle provvigioni (DEC-021), crediti scaduti e incassi
 * diversi dall'atteso (DEC-004), previsioni di saldo e imposte, email. Soglie e finestre sono parametri provvisori
 * (7 giorni, 20%) che si possono cambiare qui e dalla configurazione. Il gating reale è sul server: il Commercialista
 * legge, Admin e Amministrazione scrivono.
 */

type Tab = 'matrix' | 'pending' | 'duplicates' | 'alerts' | 'thresholds'

const TABS: { id: Tab; label: string }[] = [
  { id: 'matrix', label: 'Matrice' },
  { id: 'pending', label: 'Da approvare' },
  { id: 'duplicates', label: 'Duplicati' },
  { id: 'alerts', label: 'Alert' },
  { id: 'thresholds', label: 'Soglie soci' },
]

function errMsg(e: unknown, fallback: string): string {
  return e instanceof Error ? e.message : fallback
}

function eur(n: number | null | undefined): string {
  return n === null || n === undefined ? '—' : n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return '—'
  const [y, m, d] = s.slice(0, 10).split('-')
  return `${d}/${m}/${y}`
}

function canWriteRole(auth: LoginResponse): boolean {
  return auth.role === 'Administrator' || auth.role === 'EqualAdmin' || auth.role === 'EqualAmministrazione'
}

function levelLabel(level: ApproverLevel): string {
  return APPROVER_LEVELS.find(l => l.value === level)?.label ?? level
}

export function FinanceControlsPage({ auth }: { auth: LoginResponse }) {
  const [params, setParams] = useSearchParams()
  const requested = params.get('tab') as Tab | null
  const tab: Tab = TABS.some(t => t.id === requested) ? (requested as Tab) : 'matrix'
  const canWrite = canWriteRole(auth)

  const [categories, setCategories] = useState<ExpenseCategory[]>([])
  const [centres, setCentres] = useState<CostCentre[]>([])

  useEffect(() => {
    void (async () => {
      const [c, cc] = await Promise.all([
        expenseCategoriesApi.list(true).catch(() => []),
        costCentresApi.list().catch(() => []),
      ])
      setCategories(c)
      setCentres(cc)
    })()
  }, [])

  return (
    <Page>
      <PageHeader
        title="Controlli finanza"
        icon="verified_user"
        actions={
          <>
            <ReportExportButtons title="Controlli finanziari" />
            {!canWrite && <StatusPill tone="neutral">Sola lettura</StatusPill>}
          </>
        }
      />

      <Tabs
        aria-label="Sezioni dei controlli finanziari"
        value={tab}
        onChange={id => setParams({ tab: id })}
        items={TABS.map(t => ({ id: t.id, label: t.label }))}
      />

      {tab === 'matrix' && <MatrixTab canWrite={canWrite} categories={categories} centres={centres} />}
      {tab === 'pending' && <PendingTab categories={categories} />}
      {tab === 'duplicates' && <DuplicatesTab />}
      {tab === 'alerts' && <AlertsTab />}
      {tab === 'thresholds' && <ThresholdsTab canWrite={canWrite} />}
    </Page>
  )
}

/* ── Matrice ─────────────────────────────────────────────────────────────────────────────────── */

interface PolicyForm {
  name: string
  minAmount: string
  maxAmount: string
  expenseCategoryId: string
  costCentreId: string
  requesterUserId: string
  approverLevel: ApproverLevel
  requiredApprovals: '1' | '2'
  priority: string
  isActive: boolean
}

const EMPTY_POLICY: PolicyForm = {
  name: '', minAmount: '0', maxAmount: '', expenseCategoryId: '', costCentreId: '', requesterUserId: '',
  approverLevel: 'Amministrazione', requiredApprovals: '1', priority: '10', isActive: true,
}

function MatrixTab({ canWrite, categories, centres }: { canWrite: boolean; categories: ExpenseCategory[]; centres: CostCentre[] }) {
  const confirm = useConfirm()
  const [items, setItems] = useState<ApprovalPolicy[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [form, setForm] = useState<PolicyForm | null>(null)
  const [editId, setEditId] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setItems(await financeControlsApi.policies())
    } catch (e) {
      setError(errMsg(e, 'Impossibile caricare la matrice.'))
    } finally {
      setLoading(false)
    }
  }, [])
  useEffect(() => { void load() }, [load])

  const categoryName = (id: number | null) => (id === null ? 'Tutte' : categories.find(c => c.id === id)?.name ?? `#${id}`)
  const centreName = (id: number | null) => (id === null ? 'Tutti' : centres.find(c => c.id === id)?.name ?? `#${id}`)

  function openNew() {
    setEditId(null)
    setForm({ ...EMPTY_POLICY })
    setFormError('')
  }

  function openEdit(p: ApprovalPolicy) {
    setEditId(p.id)
    setForm({
      name: p.name,
      minAmount: String(p.minAmount),
      maxAmount: p.maxAmount === null ? '' : String(p.maxAmount),
      expenseCategoryId: p.expenseCategoryId === null ? '' : String(p.expenseCategoryId),
      costCentreId: p.costCentreId === null ? '' : String(p.costCentreId),
      requesterUserId: p.requesterUserId === null ? '' : String(p.requesterUserId),
      approverLevel: p.approverLevel,
      requiredApprovals: String(p.requiredApprovals) as '1' | '2',
      priority: String(p.priority),
      isActive: p.isActive,
    })
    setFormError('')
  }

  async function save() {
    if (!form) return
    const min = Number(form.minAmount.replace(',', '.'))
    const max = form.maxAmount.trim() === '' ? null : Number(form.maxAmount.replace(',', '.'))
    if (!form.name.trim()) { setFormError('Il nome è obbligatorio.'); return }
    if (Number.isNaN(min) || (max !== null && Number.isNaN(max))) { setFormError('Importi non validi.'); return }
    const body: ApprovalPolicyBody = {
      name: form.name.trim(),
      minAmount: min,
      maxAmount: max,
      expenseCategoryId: form.expenseCategoryId ? Number(form.expenseCategoryId) : null,
      costCentreId: form.costCentreId ? Number(form.costCentreId) : null,
      requesterUserId: form.requesterUserId ? Number(form.requesterUserId) : null,
      approverLevel: form.approverLevel,
      requiredApprovals: Number(form.requiredApprovals),
      priority: Number(form.priority) || 0,
      isActive: form.isActive,
    }
    setSaving(true)
    setFormError('')
    try {
      if (editId === null) await financeControlsApi.createPolicy(body)
      else await financeControlsApi.updatePolicy(editId, body)
      setForm(null)
      void load()
    } catch (e) {
      setFormError(errMsg(e, 'Errore durante il salvataggio.'))
    } finally {
      setSaving(false)
    }
  }

  async function remove(p: ApprovalPolicy) {
    if (!(await confirm({ title: 'Elimina regola', message: `Eliminare la regola «${p.name}»?`, confirmLabel: 'Elimina', destructive: true }))) return
    try {
      await financeControlsApi.deletePolicy(p.id)
      void load()
    } catch (e) {
      setError(errMsg(e, 'Errore durante l’eliminazione.'))
    }
  }

  return (
    <div className="d-flex flex-column gap-3">
      <Callout tone="info" role="note">
        Vale la prima regola attiva, per priorità crescente, che combacia con importo (estremi compresi), categoria, centro di
        costo e richiedente. Senza regole una spesa si approva con un solo clic, come sempre. Con due approvatori servono due
        persone diverse, entrambe diverse da chi ha inserito la spesa.
      </Callout>
      {error && <Callout tone="error">{error}</Callout>}
      {canWrite && !form && (
        <div><Button variant="filled" onClick={openNew}>Nuova regola</Button></div>
      )}

      {form && (
        <Card>
          <CardHeader title={editId === null ? 'Nuova regola' : 'Modifica regola'} />
          <CardContent>
            <FormGrid columns={4}>
              <div>
                <TextField label="Nome" maxLength={100} value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <TextField label="Importo da (€)" inputMode="decimal" value={form.minAmount}
                  onChange={e => setForm({ ...form, minAmount: e.target.value })} />
              </div>
              <div>
                <TextField label="Importo fino a (€)" inputMode="decimal" placeholder="nessun limite" value={form.maxAmount}
                  onChange={e => setForm({ ...form, maxAmount: e.target.value })} />
              </div>
              <div>
                <Select label="Categoria" value={form.expenseCategoryId}
                  onChange={e => setForm({ ...form, expenseCategoryId: e.target.value })}>
                  <option value="">Tutte</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Select>
              </div>
              <div>
                <Select label="Centro di costo" value={form.costCentreId}
                  onChange={e => setForm({ ...form, costCentreId: e.target.value })}>
                  <option value="">Tutti</option>
                  {centres.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Select>
              </div>
              <div>
                <TextField label="Id richiedente" inputMode="numeric" placeholder="qualsiasi" value={form.requesterUserId}
                  onChange={e => setForm({ ...form, requesterUserId: e.target.value.replace(/\D/g, '') })} />
              </div>
              <div>
                <Select label="Livello" value={form.approverLevel}
                  onChange={e => setForm({ ...form, approverLevel: e.target.value as ApproverLevel })}>
                  {APPROVER_LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                </Select>
              </div>
              <div>
                <Select label="Approvatori" value={form.requiredApprovals}
                  onChange={e => setForm({ ...form, requiredApprovals: e.target.value as '1' | '2' })}>
                  <option value="1">1</option>
                  <option value="2">2 distinti</option>
                </Select>
              </div>
              <div>
                <TextField label="Priorità" inputMode="numeric" value={form.priority}
                  onChange={e => setForm({ ...form, priority: e.target.value.replace(/[^\d-]/g, '') })} />
              </div>
              <div className="d-flex align-items-end">
                <Checkbox id="policy-active" label="Attiva" checked={form.isActive}
                  onChange={e => setForm({ ...form, isActive: e.target.checked })} />
              </div>
            </FormGrid>
            {formError && <div className="md-typescale-body-small text-danger mt-2">{formError}</div>}
            <div className="mt-3 d-flex gap-2">
              <Button variant="text" disabled={saving} onClick={() => setForm(null)}>Annulla</Button>
              <Button variant="tonal" disabled={saving} onClick={() => void save()}>Salva</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <LoadingState />
      ) : items.length === 0 ? (
        <EmptyState icon="inbox" title="Nessuna regola: ogni spesa si approva con un solo clic dell’Amministrazione." />
      ) : (
        <DataTable>
            <thead>
              <tr>
                <th>Priorità</th><th>Nome</th><th>Importo</th><th>Categoria</th><th>Centro</th>
                <th>Richiedente</th><th>Livello</th><th>Approvatori</th><th>Stato</th>{canWrite && <th />}
              </tr>
            </thead>
            <tbody>
              {items.map(p => (
                <tr key={p.id} className={p.isActive ? '' : 'text-muted'}>
                  <td>{p.priority}</td>
                  <td>{p.name}</td>
                  <td className="text-nowrap">{eur(p.minAmount)} – {p.maxAmount === null ? 'senza limite' : eur(p.maxAmount)}</td>
                  <td>{categoryName(p.expenseCategoryId)}</td>
                  <td>{centreName(p.costCentreId)}</td>
                  <td>{p.requesterUserId === null ? 'Chiunque' : `Utente #${p.requesterUserId}`}</td>
                  <td>{levelLabel(p.approverLevel)}</td>
                  <td>{p.requiredApprovals}</td>
                  <td>{p.isActive ? <StatusPill tone="success">Attiva</StatusPill> : <StatusPill tone="neutral">Spenta</StatusPill>}</td>
                  {canWrite && (
                    <td className="text-end text-nowrap">
                      <IconButton icon="edit" size="sm" aria-label="Modifica" onClick={() => openEdit(p)} />
                      <IconButton icon="delete" size="sm" aria-label="Elimina" onClick={() => void remove(p)} />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </DataTable>
      )}
    </div>
  )
}

/* ── Da approvare ────────────────────────────────────────────────────────────────────────────── */

function PendingTab({ categories }: { categories: ExpenseCategory[] }) {
  const [expenses, setExpenses] = useState<Expense[]>([])
  const [info, setInfo] = useState<Record<number, ApprovalInfo>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState<number | null>(null)
  const [rowError, setRowError] = useState<{ id: number; message: string } | null>(null)
  const [rejecting, setRejecting] = useState<number | null>(null)
  const [reason, setReason] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const page = await expensesApi.list({ status: 'Prevista', size: 100 })
      setExpenses(page.items)
      const entries = await Promise.all(
        page.items.map(x => financeControlsApi.approvals(x.id).then(i => [x.id, i] as const).catch(() => null)),
      )
      setInfo(Object.fromEntries(entries.filter((x): x is readonly [number, ApprovalInfo] => x !== null)))
    } catch (e) {
      setError(errMsg(e, 'Impossibile caricare le spese da approvare.'))
    } finally {
      setLoading(false)
    }
  }, [])
  useEffect(() => { void load() }, [load])

  async function decide(id: number, decision: 'Approvata' | 'Rifiutata') {
    setBusy(id)
    setRowError(null)
    try {
      await financeControlsApi.decide(id, decision, decision === 'Rifiutata' ? reason : undefined)
      setRejecting(null)
      setReason('')
      void load()
    } catch (e) {
      setRowError({ id, message: errMsg(e, 'Errore durante la decisione.') })
    } finally {
      setBusy(null)
    }
  }

  const categoryName = (id: number) => categories.find(c => c.id === id)?.name ?? `#${id}`

  return (
    <div className="d-flex flex-column gap-3">
      {error && <Callout tone="error">{error}</Callout>}
      {loading ? (
        <LoadingState />
      ) : expenses.length === 0 ? (
        <EmptyState icon="inbox" title="Nessuna spesa in attesa di approvazione." />
      ) : (
        <DataTable>
            <thead>
              <tr><th>Data</th><th>Descrizione</th><th>Categoria</th><th className="text-end">Totale</th><th>Regola</th><th>Approvazioni</th><th /></tr>
            </thead>
            <tbody>
              {expenses.map(x => {
                const i = info[x.id]
                return (
                  <tr key={x.id}>
                    <td className="text-nowrap">{fmtDate(x.expenseDate)}</td>
                    <td>{x.description}</td>
                    <td>{categoryName(x.expenseCategoryId)}</td>
                    <td className="text-end text-nowrap">{eur(x.totalAmount)}</td>
                    <td>{i ? (i.requirement.isDefault ? 'Nessuna (clic singolo)' : `${i.requirement.policyName} · ${levelLabel(i.requirement.level)}`) : '—'}</td>
                    <td>{i ? <StatusPill tone="info">{i.approvedCount}/{i.requirement.requiredApprovals}</StatusPill> : '—'}</td>
                    <td className="text-end">
                      {i?.canDecide && rejecting !== x.id && (
                        <div className="d-flex gap-1 justify-content-end">
                          <IconButton icon="check_circle" size="sm" aria-label="Approva" disabled={busy === x.id}
                            onClick={() => void decide(x.id, 'Approvata')} />
                          <IconButton icon="cancel" size="sm" aria-label="Rifiuta" disabled={busy === x.id}
                            onClick={() => { setRejecting(x.id); setReason('') }} />
                        </div>
                      )}
                      {i?.canDecide && rejecting === x.id && (
                        <div className="d-flex gap-1 justify-content-end">
                          <TextField label="Motivo del rifiuto" labelHidden placeholder="Motivo del rifiuto" maxLength={500}
                            value={reason} onChange={e => setReason(e.target.value)} />
                          <Button size="sm" variant="text" disabled={busy === x.id || !reason.trim()}
                            onClick={() => void decide(x.id, 'Rifiutata')}>Conferma</Button>
                          <Button size="sm" variant="text" onClick={() => setRejecting(null)}>Annulla</Button>
                        </div>
                      )}
                      {i && !i.canDecide && <span className="text-muted md-typescale-body-small">Non spetta a te</span>}
                      {rowError?.id === x.id && <div className="text-danger md-typescale-body-small mt-1">{rowError.message}</div>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </DataTable>
      )}
    </div>
  )
}

/* ── Duplicati ───────────────────────────────────────────────────────────────────────────────── */

function DuplicatesTab() {
  const [windowDays, setWindowDays] = useState('7')
  const [data, setData] = useState<DuplicatesResponse | null>(null)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setError('')
    try {
      const w = Number(windowDays)
      setData(await financeControlsApi.duplicates(Number.isNaN(w) ? undefined : { windowDays: w }))
    } catch (e) {
      setError(errMsg(e, 'Impossibile caricare i duplicati.'))
    }
  }, [windowDays])
  useEffect(() => { void load() }, [load])

  return (
    <div className="d-flex flex-column gap-3">
      <Callout tone="info" role="note">
        Due spese sono probabilmente duplicate se hanno lo stesso fornitore (o, senza fornitore, la stessa descrizione), lo stesso
        totale e la data entro la finestra qui sotto (valore provvisorio). Le annullate non contano. Si guardano le spese
        degli ultimi {data?.days ?? 90} giorni.
      </Callout>
      <FilterBar>
        <TextField id="dup-window" label="Finestra (giorni)" className="md-u-maxw-140" inputMode="numeric"
          value={windowDays} onChange={e => setWindowDays(e.target.value.replace(/\D/g, ''))} />
      </FilterBar>
      {error && <Callout tone="error">{error}</Callout>}
      {data && data.groups.length === 0 && (
        <EmptyState icon="inbox" title="Nessuna spesa duplicata." />
      )}
      {data?.groups.map(g => (
        <Card key={g.expenseIds[0]} padding="sm">
          <div>
            <div className="md-typescale-emphasized-body-small mb-1">{g.expenseIds.length} spese simili</div>
            <ul className="mb-0 md-typescale-body-small">
              {g.expenses.map(x => (
                <li key={x.id}>#{x.id} · {fmtDate(x.expenseDate)} · {x.description} · {eur(x.totalAmount)} · {x.status}</li>
              ))}
            </ul>
          </div>
        </Card>
      ))}
    </div>
  )
}

/* ── Alert ───────────────────────────────────────────────────────────────────────────────────── */

const ALERT_LABELS: Record<string, string> = {
  incasso_non_riconciliato: 'Incasso non riconciliato',
  saldo_socio_oltre_soglia: 'Saldo socio oltre soglia',
  budget_superato: 'Budget superato',
}

function AlertsTab() {
  const [days, setDays] = useState('7')
  const [pct, setPct] = useState('20')
  const [data, setData] = useState<AlertsResponse | null>(null)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setError('')
    try {
      const d = Number(days)
      const p = Number(pct.replace(',', '.'))
      setData(await financeControlsApi.alerts({
        days: Number.isNaN(d) ? undefined : d,
        pct: Number.isNaN(p) ? undefined : p,
      }))
    } catch (e) {
      setError(errMsg(e, 'Impossibile caricare gli alert.'))
    }
  }, [days, pct])
  useEffect(() => { void load() }, [load])

  return (
    <div className="d-flex flex-column gap-3">
      <Callout tone="info" role="note">
        Gli alert si calcolano ogni volta che apri la pagina. L’invio automatico delle notifiche a chi amministra è un
        servizio a parte, spento finché non viene attivato nella configurazione. Soglie provvisorie: 7 giorni e 20%.
      </Callout>
      <FilterBar>
        <TextField id="al-days" label="Incasso non riconciliato da (giorni)" inputMode="numeric"
          value={days} onChange={e => setDays(e.target.value.replace(/\D/g, ''))} />
        <TextField id="al-pct" label="Budget superato di almeno (%)" inputMode="decimal"
          value={pct} onChange={e => setPct(e.target.value)} />
      </FilterBar>
      {error && <Callout tone="error">{error}</Callout>}
      {data && data.items.length === 0 && (
        <EmptyState icon="notifications_off" title={`Nessun alert al ${fmtDate(data.asOf)}.`} />
      )}
      {data && data.items.length > 0 && (
        <DataTable>
            <thead><tr><th>Tipo</th><th>Dettaglio</th><th>Valori</th></tr></thead>
            <tbody>
              {data.items.map(a => (
                <tr key={a.key}>
                  <td><StatusPill tone="warning">{ALERT_LABELS[a.type] ?? a.type}</StatusPill></td>
                  <td>{a.message}</td>
                  <td className="md-typescale-body-small text-muted">
                    {a.type === 'saldo_socio_oltre_soglia' && <>Saldo {eur(a.balance)} · soglia {eur(a.threshold)}</>}
                    {a.type === 'budget_superato' && <>Budget {eur(a.budget)} · consuntivo {eur(a.actual)}</>}
                    {a.type === 'incasso_non_riconciliato' && <>Movimento #{a.entityId}</>}
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
      )}
    </div>
  )
}

/* ── Soglie soci ─────────────────────────────────────────────────────────────────────────────── */

function ThresholdsTab({ canWrite }: { canWrite: boolean }) {
  const [data, setData] = useState<AlertsResponse | null>(null)
  const [drafts, setDrafts] = useState<Record<number, string>>({})
  const [error, setError] = useState('')
  const [rowError, setRowError] = useState<{ id: number; message: string } | null>(null)

  const load = useCallback(async () => {
    setError('')
    try {
      const res = await financeControlsApi.alerts()
      setData(res)
      setDrafts(Object.fromEntries(res.partnerThresholds.map(p => [p.partnerId, p.threshold === null ? '' : String(p.threshold)])))
    } catch (e) {
      setError(errMsg(e, 'Impossibile caricare le soglie.'))
    }
  }, [])
  useEffect(() => { void load() }, [load])

  async function save(partnerId: number) {
    const raw = (drafts[partnerId] ?? '').trim()
    const value = raw === '' ? null : Number(raw.replace(',', '.'))
    if (value !== null && (Number.isNaN(value) || value < 0)) {
      setRowError({ id: partnerId, message: 'Soglia non valida.' })
      return
    }
    setRowError(null)
    try {
      await financeControlsApi.setPartnerThreshold(partnerId, value)
      void load()
    } catch (e) {
      setRowError({ id: partnerId, message: errMsg(e, 'Errore durante il salvataggio.') })
    }
  }

  return (
    <div className="d-flex flex-column gap-3">
      <Callout tone="info" role="note">
        Se il saldo di un socio supera la sua soglia (in valore assoluto, in un senso o nell’altro) compare un alert e una voce nella
        coda Amministrazione. Soglia vuota = nessun controllo. Saldo positivo: l’azienda deve al socio.
      </Callout>
      {error && <Callout tone="error">{error}</Callout>}
      {data && data.partnerThresholds.length === 0 && (
        <EmptyState icon="inbox" title="Nessun socio attivo." />
      )}
      {data && data.partnerThresholds.length > 0 && (
        <DataTable>
            <thead><tr><th>Socio</th><th className="text-end">Saldo</th><th>Soglia (€)</th>{canWrite && <th />}</tr></thead>
            <tbody>
              {data.partnerThresholds.map(p => (
                <tr key={p.partnerId}>
                  <td>{p.name}</td>
                  <td className="text-end text-nowrap">{p.balance === null ? '—' : eur(p.balance)}</td>
                  <td>
                    {canWrite ? (
                      <TextField label={`Soglia ${p.name}`} labelHidden className="md-u-maxw-160" inputMode="decimal" placeholder="nessuna"
                        value={drafts[p.partnerId] ?? ''} onChange={e => setDrafts({ ...drafts, [p.partnerId]: e.target.value })} />
                    ) : (p.threshold === null ? 'Nessuna' : eur(p.threshold))}
                  </td>
                  {canWrite && (
                    <td>
                      <Button size="sm" variant="text" onClick={() => void save(p.partnerId)}>Salva</Button>
                      {rowError?.id === p.partnerId && <span className="text-danger md-typescale-body-small ms-2">{rowError.message}</span>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </DataTable>
      )}
    </div>
  )
}
